import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styles from './Blog.module.css';
import '@fortawesome/fontawesome-free/css/all.min.css';

const API_URL = 'https://yeskondomedia.com/ymplblogsadmin/api.php';

const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('en-US', options);
};

const readTime = (text = '') => {
  const words = text.replace(/<[^>]*>/g, '').split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 200));
};

/* ─ Sidebar ─ */
const DetailSidebar = ({ blog }) => {
  const [recentBlogs, setRecentBlogs] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}?action=get_blogs`)
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success') {
          setRecentBlogs(data.data.filter(b => b.slug !== blog?.slug).slice(0, 4));
        }
      })
      .catch(() => {});
  }, [blog]);

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href).then(() => alert('Link copied!'));
  };

  return (
    <aside className={styles.sidebar}>

      {/* Share */}
      <div className={styles.sidebar_widget}>
        <h3 className={styles.widget_title}>Share Article</h3>
        <div className={styles.share_buttons}>
          {[
            { icon: 'fab fa-facebook-f', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, label: 'Facebook' },
            { icon: 'fab fa-whatsapp', href: `https://wa.me/?text=${encodeURIComponent((blog?.title || '') + ' ' + window.location.href)}`, label: 'WhatsApp' },
            { icon: 'fab fa-linkedin-in', href: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(blog?.title || '')}`, label: 'LinkedIn' },
            { icon: 'fab fa-twitter', href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(blog?.title || '')}&url=${encodeURIComponent(window.location.href)}`, label: 'Twitter' },
          ].map((s, i) => (
            <a key={i} href={s.href} target="_blank" rel="noreferrer" className={styles.share_btn} aria-label={s.label}>
              <i className={s.icon}></i>
            </a>
          ))}
          <button className={styles.share_btn} onClick={handleCopy} aria-label="Copy link" title="Copy link">
            <i className="fas fa-link"></i>
          </button>
        </div>
      </div>

      {/* About */}
      <div className={styles.sidebar_widget}>
        <h3 className={styles.widget_title}>About Yeskondo</h3>
        <p className={styles.about_widget_text}>
          Yeskondo Media Pvt. Ltd. is Pune's best 360° marketing agency. We help
          brands grow through digital campaigns, OOH, BTL/ATL, events and more.
        </p>
        <Link to="/contact" className={styles.widget_cta}>Get a Free Quote →</Link>
      </div>

      {/* Recent Posts */}
      {recentBlogs.length > 0 && (
        <div className={styles.sidebar_widget}>
          <h3 className={styles.widget_title}>More Articles</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {recentBlogs.map((b, i) => {
              let img = b.image_url;
              if (img && !img.startsWith('http')) img = `https://yeskondomedia.com/ymplblogsadmin/image.php?path=${img}`;
              const fallback = 'https://images.unsplash.com/photo-1432821596592-e2c18b78144f?auto=format&fit=crop&w=120&q=60';
              return (
                <Link key={i} to={`/blog/${b.slug}`} style={{ display: 'flex', gap: '12px', textDecoration: 'none', alignItems: 'flex-start' }}>
                  <img src={img || fallback} alt={b.title} loading="lazy"
                    style={{ width: '58px', height: '48px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0 }}
                    onError={(e) => { e.target.src = fallback; }}
                  />
                  <div>
                    <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-color)', lineHeight: 1.35, marginBottom: '4px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {b.title}
                    </p>
                    <p style={{ fontSize: '0.72rem', color: '#aaa', margin: 0 }}>{formatDate(b.created_at)}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className={styles.sidebar_widget} style={{ background: 'linear-gradient(135deg,rgba(255,165,0,0.08),rgba(255,107,53,0.05))', borderColor: 'rgba(255,165,0,0.2)' }}>
        <h3 className={styles.widget_title}>Need Marketing Help?</h3>
        <p className={styles.about_widget_text}>
          Let Pune's best marketing company craft a powerful strategy for your brand. Free consultation — no strings attached.
        </p>
        <a href="tel:+919922966739" className={styles.widget_cta}>📞 Call Now →</a>
      </div>

    </aside>
  );
};

/* ═══════════════════════════════════
   MAIN BlogDetail Component
═══════════════════════════════════ */
const BlogDetail = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API_URL}?action=get_blog_detail&slug=${id}`)
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success') {
          if (data.data.content) {
            data.data.content = data.data.content.replace(
              /src="uploads\//g,
              'src="https://yeskondomedia.com/ymplblogsadmin/image.php?path=uploads/'
            );
          }
          setBlog(data.data);
          if (data.data.schema_markup) {
            const script = document.createElement('script');
            script.type = 'application/ld+json';
            script.innerHTML = data.data.schema_markup;
            script.id = 'blog-schema';
            document.head.appendChild(script);
          }
        } else {
          setError(data.message || 'Blog not found');
        }
        setLoading(false);
      })
      .catch(() => { setError('Failed to load blog post.'); setLoading(false); });

    return () => {
      const s = document.getElementById('blog-schema');
      if (s) s.remove();
    };
  }, [id]);

  /* ── Loading ── */
  if (loading) {
    return (
      <div className={styles.blog_page}>
        <div className={styles.loading_state}>
          <div className={styles.loading_spinner}></div>
          <p className={styles.loading_text}>Loading article…</p>
        </div>
      </div>
    );
  }

  /* ── Error ── */
  if (error || !blog) {
    return (
      <div className={styles.blog_page}>
        <div className={styles.error_state}>
          <div className={styles.error_emoji}>📄</div>
          <h1 className={styles.error_title}>Article Not Found</h1>
          <p className={styles.error_msg}>{error || "We couldn't find this article. It may have been moved or deleted."}</p>
          <Link to="/blog" className={styles.error_btn}>← Back to All Articles</Link>
        </div>
      </div>
    );
  }

  let imageUrl = blog.image_url;
  if (imageUrl && !imageUrl.startsWith('http')) {
    imageUrl = `https://yeskondomedia.com/ymplblogsadmin/image.php?path=${imageUrl}`;
  }

  const pageTitle = blog.seo_title || blog.title;
  const pageDesc = blog.seo_description || blog.title;
  const pageUrl = blog.canonical_url || `https://www.yeskondomedia.com/blog/${id}`;

  return (
    <>
      <Helmet>
        <title>{pageTitle} | Yeskondo Media Blog</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        {imageUrl && <meta property="og:image" content={imageUrl} />}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        {imageUrl && <meta name="twitter:image" content={imageUrl} />}
      </Helmet>

      <div className={styles.blog_page}>

        {/* ══ 1. DARK HERO BANNER ══
            Breadcrumb → Category → H1 → Author/Date
            All on dark background — clear visual separation */}
        <div className={styles.article_hero}>
          <div className={styles.article_hero_inner}>

            <nav className={styles.hero_breadcrumb} aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span className={styles.hero_breadcrumb_sep}>›</span>
              <Link to="/blog">Blog</Link>
              <span className={styles.hero_breadcrumb_sep}>›</span>
              <span className={styles.hero_breadcrumb_current}>{blog.category || 'Marketing'}</span>
            </nav>

            <span className={styles.hero_category} itemProp="articleSection">
              {blog.category || 'Marketing'}
            </span>

            <h1 className={styles.hero_title} itemProp="headline">
              {blog.title}
            </h1>

            <div className={styles.hero_meta}>
              <div className={styles.hero_author_avatar} aria-hidden="true">Y</div>
              <div className={styles.hero_author_info}>
                <div className={styles.hero_author_name} itemProp="author">Yeskondo Media Team</div>
                <div className={styles.hero_author_role}>Best Marketing Company in Pune</div>
              </div>
              <div className={styles.hero_date}>
                <span itemProp="datePublished" content={blog.created_at}>
                  {formatDate(blog.created_at)}
                </span>
                {' · '}{readTime(blog.content)} min read
              </div>
            </div>

          </div>
        </div>

        {/* ══ 2. CONTENT AREA: Article + Sidebar ══ */}
        <div className={styles.detail_layout}>

          <article className={styles.article_wrap} itemScope itemType="https://schema.org/BlogPosting">

            {/* Cover image */}
            {imageUrl && (
              <img
                src={imageUrl}
                alt={blog.title}
                className={styles.article_cover}
                itemProp="image"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}

            {/* Article body */}
            <div className={styles.article_body}>
              <div
                className={styles.article_content}
                itemProp="articleBody"
                dangerouslySetInnerHTML={{ __html: blog.content }}
              />
            </div>

            {/* Footer: back + share */}
            <footer className={styles.article_footer}>
              <Link to="/blog" className={styles.back_link}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M19 12H5M12 5l-7 7 7 7" />
                </svg>
                All Articles
              </Link>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className={styles.share_label}>Share:</span>
                <div className={styles.share_buttons}>
                  {[
                    { icon: 'fab fa-facebook-f', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, label: 'Facebook' },
                    { icon: 'fab fa-whatsapp', href: `https://wa.me/?text=${encodeURIComponent(blog.title + ' ' + window.location.href)}`, label: 'WhatsApp' },
                    { icon: 'fab fa-linkedin-in', href: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(blog.title)}`, label: 'LinkedIn' },
                    { icon: 'fab fa-twitter', href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(blog.title)}&url=${encodeURIComponent(window.location.href)}`, label: 'Twitter' },
                  ].map((s, i) => (
                    <a key={i} href={s.href} target="_blank" rel="noreferrer" className={styles.share_btn} aria-label={`Share on ${s.label}`}>
                      <i className={s.icon}></i>
                    </a>
                  ))}
                </div>
              </div>
            </footer>

          </article>

          <DetailSidebar blog={blog} />

        </div>
      </div>
    </>
  );
};

export default BlogDetail;
